/**
 * Household-document factory — Story 23.2 QA fix (MNT-23.2-1)
 *
 * Single source of truth for the *initial* `households/{uid}` write shape, shared
 * by every account-creation path so the GDPR-critical consent + account-default
 * fields can never silently diverge between surfaces:
 *   - email signup   → `SignupPage.onSubmit`
 *   - social signup  → `CompleteSignupPage.handleContinue` (Google now, Facebook S23.7)
 *
 * The shape mirrors the `Household` read type (docs/firestore-schema.md is the
 * authoritative source) but the server-stamped timestamps are `serverTimestamp()`
 * sentinels at write time, so the return is a *write* shape (`FieldValue`) rather
 * than the resolved read shape `Household` (Date/Timestamp). No schema change.
 *
 * @module lib/household
 */

import { serverTimestamp, type DocumentData, type FieldValue } from 'firebase/firestore';
import type { ZodError } from 'zod';
import type { ChildProfile, Household } from '@sagamoon/shared/types';
import { HouseholdSchema } from '@sagamoon/shared';

/** Current Terms-of-Service version recorded with consent (FR78). */
export const TERMS_OF_SERVICE_VERSION = 'v1.0';

/** Inputs that vary per account; everything else is a fixed beta default. */
export interface NewHouseholdInput {
  /** Firebase Auth UID — also the household document id. */
  uid: string;
  /** Family-account email (the email-form value, or the social provider's email). */
  email: string;
}

/**
 * The exact field set written to `households/{uid}` on account creation.
 * Server-stamped timestamps are `FieldValue` (`serverTimestamp()` sentinels).
 */
export interface NewHouseholdDoc {
  id: string;
  email: string;
  children: ChildProfile[];
  consents: {
    termsOfService: {
      accepted: true;
      acceptedAt: FieldValue;
      version: string;
    };
  };
  preferences: {
    language: 'fi';
    analyticsOptOut: boolean;
    emailNotifications: boolean;
  };
  usageQuotas: {
    stories: { count: number; resetAt: FieldValue };
    photos: { count: number; resetAt: FieldValue };
    reads: { count: number; resetAt: FieldValue };
  };
  firstStoryCreated: boolean;
  accountType: 'family';
  createdAt: FieldValue;
  updatedAt: FieldValue;
}

/**
 * Build the initial `households/{uid}` document for a brand-new account.
 *
 * Used verbatim by both the email-signup and social-onboarding paths so the
 * consent record + account defaults stay byte-identical across surfaces. Only
 * `id` and `email` vary per account.
 *
 * @param input - Per-account uid + email
 * @returns The write payload for `setDoc(doc(db, 'households', uid), …)`
 */
export function buildNewHouseholdDoc({ uid, email }: NewHouseholdInput): NewHouseholdDoc {
  return {
    id: uid,
    email,
    children: [],
    // GDPR consent tracking with versioning (FR78)
    consents: {
      termsOfService: {
        accepted: true,
        acceptedAt: serverTimestamp(),
        version: TERMS_OF_SERVICE_VERSION, // Track which version user accepted
      },
      // photoProcessing: set when user uploads first photo (FR36)
    },
    preferences: {
      language: 'fi',
      analyticsOptOut: false,
      emailNotifications: true,
    },
    usageQuotas: {
      stories: { count: 0, resetAt: serverTimestamp() },
      photos: { count: 0, resetAt: serverTimestamp() },
      reads: { count: 0, resetAt: serverTimestamp() },
    },
    firstStoryCreated: false,
    // Epic 7C.2: Account type for metrics filtering (default to 'family')
    accountType: 'family',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

/**
 * Normalize a raw `households/{uid}` Firestore document into a validated `Household`.
 *
 * Story 23.9 — the single source of truth for *reading* a household into the store,
 * shared by the global `onAuthStateChanged` listener and every auth-page path
 * (email login, Google login/signup, social onboarding). Replaces the per-path
 * `setHousehold(doc.data() as Household)` shortcut (PATTERN-001): that cast does
 * **zero** runtime work, so raw Firestore `Timestamp`s land in `Date`-typed fields
 * and later crash consumers like the feedback-cadence engine (`x.getTime` is not a
 * function). This routine does the real work instead:
 *   1. converts every `Timestamp`→`Date` (incl. `feedbackState.*`, which the old
 *      inline listener block omitted — see Story 23.9 AC5),
 *   2. normalizes the legacy `consentTimestamps`→`consents` shape,
 *   3. applies the same per-field defaults,
 *   4. runs `HouseholdSchema.safeParse`.
 *
 * Pure: no I/O (no `getDoc`) and no store access, so callers keep their own
 * control flow (`.exists()` checks, navigate timing) and own their `setHousehold`
 * / `logError` branch. Trivially unit-testable.
 *
 * @param raw - The raw `householdDoc.data()` object from a Firestore read.
 * @param onValidationError - Optional hook invoked with the Zod error when
 *   `safeParse` fails, immediately before returning `null`. Lets a caller log the
 *   field-level validation detail (the global `onAuthStateChanged` listener does —
 *   Story 23.9 QA fix OBS-23.9-1) without this routine taking on a Sentry/store
 *   dependency, so it stays pure and trivially unit-testable.
 * @returns The validated `Household` on success, or `null` if `safeParse` fails
 *   (caller logs + decides how to handle the null).
 */
export function normalizeHouseholdDoc(
  raw: DocumentData,
  onValidationError?: (error: ZodError) => void
): Household | null {
  // Convert Firestore Timestamps to Dates for Zod validation
  // Handles both new format (consents) and legacy format (consentTimestamps)
  // for backward compatibility with existing test account
  const householdData = {
    ...raw,
    createdAt: raw.createdAt?.toDate?.() || new Date(),
    updatedAt: raw.updatedAt?.toDate?.() || new Date(),
    children:
      raw.children?.map((child: ChildProfile) => ({
        ...child,
      })) || [],
    // New consent structure (matches firestore-schema.md)
    consents: raw.consents
      ? {
          termsOfService: {
            accepted: raw.consents.termsOfService?.accepted ?? true,
            acceptedAt: raw.consents.termsOfService?.acceptedAt?.toDate?.() || new Date(),
            version: raw.consents.termsOfService?.version || 'v1.0',
          },
          ...(raw.consents.photoProcessing && {
            photoProcessing: {
              accepted: raw.consents.photoProcessing.accepted ?? true,
              acceptedAt: raw.consents.photoProcessing.acceptedAt?.toDate?.() || new Date(),
              explanation:
                raw.consents.photoProcessing.explanation || 'Photos processed via Gemini 2.5 Flash',
            },
          }),
        }
      : // Legacy fallback for old consentTimestamps format
        {
          termsOfService: {
            accepted: true,
            acceptedAt:
              raw.consentTimestamps?.termsOfService?.toDate?.() ||
              raw.consentTimestamps?.termsAccepted?.toDate?.() ||
              new Date(),
            version: 'v1.0',
          },
          ...(raw.consentTimestamps?.photoProcessing && {
            photoProcessing: {
              accepted: true,
              acceptedAt: raw.consentTimestamps.photoProcessing.toDate?.() || new Date(),
              explanation: 'Photos processed via Gemini 2.5 Flash',
            },
          }),
        },
    usageQuotas: {
      stories: {
        count: raw.usageQuotas?.stories?.count || 0,
        resetAt: raw.usageQuotas?.stories?.resetAt?.toDate?.() || new Date(),
      },
      photos: {
        count: raw.usageQuotas?.photos?.count || 0,
        resetAt: raw.usageQuotas?.photos?.resetAt?.toDate?.() || new Date(),
      },
      reads: {
        count: raw.usageQuotas?.reads?.count || 0,
        resetAt: raw.usageQuotas?.reads?.resetAt?.toDate?.() || new Date(),
      },
    },
    preferences: {
      language: raw.preferences?.language || 'fi',
      analyticsOptOut: raw.preferences?.analyticsOptOut ?? false,
      emailNotifications: raw.preferences?.emailNotifications ?? true,
    },
    // Optional fields
    firstStoryCreated: raw.firstStoryCreated ?? false,
    onboardingCompletedAt: raw.onboardingCompletedAt?.toDate?.() || undefined,
    betaCohort: raw.betaCohort || undefined,
    invitationCode: raw.invitationCode || undefined,
    // Story 7A.4: Password change fields for pre-created beta accounts
    requiresPasswordChange: raw.requiresPasswordChange ?? false,
    passwordChangedAt: raw.passwordChangedAt?.toDate?.() || undefined,
    // Story 23.9 (AC5): convert feedbackState timestamps too — these are z.date()
    // in HouseholdFeedbackStateSchema and are read via .getTime() in
    // feedback-cadence.ts. The old inline listener block carried feedbackState
    // through untouched via ...rawData, so a household with lastPulseAt set
    // failed safeParse (Timestamp ≠ Date) and was rendered household-less.
    feedbackState: raw.feedbackState
      ? {
          ...raw.feedbackState,
          lastStoryPromptAt: raw.feedbackState.lastStoryPromptAt?.toDate?.() || undefined,
          lastPulseAt: raw.feedbackState.lastPulseAt?.toDate?.() || undefined,
        }
      : undefined,
  };

  // Validate household data structure (QA Fix: TYPE-001)
  const validationResult = HouseholdSchema.safeParse(householdData);

  if (validationResult.success) {
    return validationResult.data as Household;
  }

  // Invalid household data structure — caller owns the logError + setHousehold(null).
  // Surface the field-level Zod errors so a caller can log them (the global listener
  // does — Story 23.9 QA fix OBS-23.9-1) without this routine taking on a Sentry/store
  // dependency, keeping it pure and trivially unit-testable.
  onValidationError?.(validationResult.error);
  return null;
}
